import { Loader2, Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { avatarOf, fileUrl } from "../lib/api";

export const Loader = ({ full }) => (
  <div data-testid="loader" className={`flex items-center justify-center ${full ? "min-h-screen" : "py-16"}`}>
    <Loader2 className="h-7 w-7 animate-spin text-[#E4F222]" />
  </div>
);

export const Logo = ({ where = "app", size, max, path }) => {
  const { settings } = useAuth();
  let px = size || (where === "login" ? settings?.logo_size_login : settings?.logo_size_app) || (where === "login" ? 80 : 44);
  if (max) px = Math.min(px, max);
  const src = path !== undefined ? path : settings?.logo_path;
  if (src) return <img data-testid={`app-logo-${where}`} src={fileUrl(src)} alt="logo" style={{ height: px }} className="w-auto max-w-full object-contain" />;
  return (
    <div data-testid={`app-logo-${where}`} className="leading-none select-none" style={{ height: px }}>
      <div className="font-display font-black italic tracking-tight" style={{ fontSize: px * 0.68, lineHeight: 1 }}>
        alta<span className="text-[#E4F222]">X</span>
      </div>
      <div className="font-display font-bold italic text-[#94A3B8]" style={{ fontSize: Math.max(8, px * 0.2), letterSpacing: "0.55em", marginTop: px * 0.04 }}>CIRCUITO</div>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, action, eyebrow }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4 rise">
    <div>
      {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
      <h1 data-testid="page-title" className="h-display text-4xl sm:text-5xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-[#94A3B8]">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const PillTabs = ({ tabs, value, onChange, testid = "pill" }) => (
  <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
    {tabs.map((t) => {
      const v = typeof t === "string" ? t : t.value;
      const l = typeof t === "string" ? t : t.label;
      const active = v === value;
      return (
        <button
          key={v}
          data-testid={`${testid}-${String(v || "all").toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onChange(v)}
          className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-[background-color,color] ${
            active ? "bg-[#E4F222] text-black" : "bg-[#121722] text-[#94A3B8] border border-[#232B3E] hover:text-white"
          }`}
        >
          {l}
        </button>
      );
    })}
  </div>
);

export const Avatar = ({ user, size = 44, ring, crown }) => {
  const src = avatarOf(user);
  const initials = (user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {crown && <Crown className="absolute -top-4 left-1/2 h-5 w-5 -translate-x-1/2 fill-[#E4F222] text-[#E4F222]" />}
      {src ? (
        <img src={src} alt={user?.name} className={`h-full w-full rounded-full object-cover ${ring ? "ring-2 ring-[#E4F222]" : ""}`} />
      ) : (
        <div className={`flex h-full w-full items-center justify-center rounded-full bg-[#1A202E] font-display font-bold text-[#E4F222] ${ring ? "ring-2 ring-[#E4F222]" : ""}`} style={{ fontSize: size * 0.36 }}>
          {initials}
        </div>
      )}
      {user?.badge_emoji && (
        <span className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-[#0B0E14] border border-[#232B3E]" style={{ width: size * 0.45, height: size * 0.45, fontSize: size * 0.26 }}>
          {user.badge_emoji}
        </span>
      )}
    </div>
  );
};

export const LevelBadge = ({ level }) =>
  level ? <span className="rounded-full border border-[#E4F222]/30 bg-[#E4F222]/10 px-2 py-0.5 text-[11px] font-semibold text-[#E4F222]">{level}</span> : null;

const STATUS_STYLE = {
  pago: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  pendente: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
  atrasado: "bg-red-500/15 text-red-400 border-red-500/30",
  aguardando: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

export const StatusPill = ({ status, label, testid }) => (
  <span data-testid={testid} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[status] || "bg-[#1A202E] text-[#94A3B8] border-[#232B3E]"}`}>
    {label}
  </span>
);

export const Empty = ({ icon: Icon, title, text, action }) => (
  <div data-testid="empty-state" className="card-x flex flex-col items-center px-6 py-14 text-center">
    {Icon && <Icon className="mb-3 h-10 w-10 text-[#E4F222]" strokeWidth={1.5} />}
    <div className="h-display text-2xl">{title}</div>
    {text && <p className="mt-1 max-w-sm text-sm text-[#94A3B8]">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{label}</span>
    {children}
  </label>
);
